import { useState, useEffect } from 'react';
import supabase from '../supabase';
import { theme, font, inputStyle, labelStyle, pillButton, outlineButton, ghostButton } from './adminTheme';
import { uploadToB2 } from '../utils/uploadToB2';
import { useAdminLang } from './AdminLangContext';
import '../design-system.css';

const T = {
    fr: {
        heading: 'Sceau animé (coin inférieur droit)',
        active: '· actif',
        desc: 'Petit dessin cliquable, visible sur tout le site.',
        image: 'Image (PNG/SVG recommandé)',
        uploading: 'Upload en cours...',
        link: 'Lien (page ou URL externe)',
        linkPlaceholder: '/signup ou https://longden.co',
        alt: 'Message alt (survol / accessibilité)',
        altPlaceholder: 'Rejoins winston.ie',
        save: 'Sauvegarder',
        saving: 'Sauvegarde...',
        activate: 'Activer sur le site',
        deactivate: 'Désactiver',
        loading: 'Chargement...',
        uploadError: (msg) => `Erreur upload : ${msg}`,
        imageUploaded: 'Image uploadée',
        saveError: (msg) => `Erreur : ${msg}`,
        activated: 'Sceau activé',
        savedDisabled: 'Sauvegardé (désactivé)',
    },
    en: {
        heading: 'Animated seal (bottom-right corner)',
        active: '· active',
        desc: 'Small clickable drawing, visible across the whole site.',
        image: 'Image (PNG/SVG recommended)',
        uploading: 'Uploading...',
        link: 'Link (page or external URL)',
        linkPlaceholder: '/signup or https://longden.co',
        alt: 'Alt text (hover / accessibility)',
        altPlaceholder: 'Join winston.ie',
        save: 'Save',
        saving: 'Saving...',
        activate: 'Activate on the site',
        deactivate: 'Deactivate',
        loading: 'Loading...',
        uploadError: (msg) => `Upload error: ${msg}`,
        imageUploaded: 'Image uploaded',
        saveError: (msg) => `Error: ${msg}`,
        activated: 'Seal activated',
        savedDisabled: 'Saved (disabled)',
    },
};

function AdminSeal() {
    const { lang } = useAdminLang();
    const t = T[lang] || T.fr;
    const [form, setForm] = useState({ image_url: '', link_url: '', alt_text: 'winston.ie', enabled: false });
    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [statusMsg, setStatusMsg] = useState('');
    const [loading, setLoading] = useState(true);

    const fetchSeal = async () => {
        setLoading(true);
        const { data } = await supabase.from('site_seal').select('*').maybeSingle();
        if (data) {
            setForm({
                image_url: data.image_url || '',
                link_url: data.link_url || '',
                alt_text: data.alt_text || 'winston.ie',
                enabled: data.enabled || false,
            });
        }
        setLoading(false);
    };

    useEffect(() => { fetchSeal(); }, []);

    const handleUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploading(true);
        try {
            const fileName = `seal-${Date.now()}.${file.name.split('.').pop()}`;
            const url = await uploadToB2(`covers/${fileName}`, file);
            setForm(f => ({ ...f, image_url: url }));
            setStatusMsg(t.imageUploaded);
        } catch (err) {
            setStatusMsg(t.uploadError(err.message));
        }
        setUploading(false);
    };

    const handleSave = async (publish) => {
        setSaving(true);
        const payload = {
            image_url: form.image_url,
            link_url: form.link_url || null,
            alt_text: form.alt_text || 'winston.ie',
            enabled: publish,
        };

        const { data: existing } = await supabase.from('site_seal').select('*').limit(1).maybeSingle();

        let error;
        if (existing) ({ error } = await supabase.from('site_seal').update(payload).eq('id', existing.id));
        else ({ error } = await supabase.from('site_seal').insert(payload));

        if (error) setStatusMsg(t.saveError(error.message));
        else {
            setForm(f => ({ ...f, enabled: publish }));
            setStatusMsg(publish ? t.activated : t.savedDisabled);
        }
        setSaving(false);
    };

    if (loading) return <p style={{ color: theme.textMuted, fontFamily: font, fontSize: '12px' }}>{t.loading}</p>;

    return (
        <div style={{ maxWidth: '400px' }}>
            <p style={{ ...labelStyle, marginBottom: '4px' }}>
                {t.heading} {form.enabled && <span style={{ color: theme.textMuted }}>{t.active}</span>}
            </p>
            <p style={{ fontFamily: font, fontSize: '11px', color: theme.textMuted, margin: '0 0 20px' }}>
                {t.desc}
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div>
                    <label style={labelStyle}>{t.image}</label>
                    {form.image_url && (
                        <div style={{ width: '80px', height: '80px', marginBottom: '8px', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img src={form.image_url} alt="preview" style={{ maxWidth: '100%', maxHeight: '100%' }} />
                        </div>
                    )}
                    <input type="file" accept="image/*" onChange={handleUpload} disabled={uploading} style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font }} />
                    {uploading && <p style={{ fontSize: '10px', color: theme.textMuted, margin: '4px 0 0' }}>{t.uploading}</p>}
                </div>

                <div>
                    <label style={labelStyle}>{t.link}</label>
                    <input value={form.link_url} onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))} style={inputStyle} placeholder={t.linkPlaceholder} />
                </div>

                <div>
                    <label style={labelStyle}>{t.alt}</label>
                    <input value={form.alt_text} onChange={e => setForm(f => ({ ...f, alt_text: e.target.value }))} style={inputStyle} placeholder={t.altPlaceholder} />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                    <button className="wlink" onClick={() => handleSave(false)} disabled={saving} style={outlineButton}>{t.save}</button>
                    <button className="wlink" onClick={() => handleSave(true)} disabled={saving} style={pillButton}>
                        {saving ? t.saving : t.activate}
                    </button>
                </div>

                {form.enabled && <button className="wlink" onClick={() => handleSave(false)} style={ghostButton}>{t.deactivate}</button>}

                {statusMsg && <p style={{ fontSize: '11px', color: theme.textMuted, fontFamily: font, margin: 0 }}>{statusMsg}</p>}
            </div>
        </div>
    );
}

export default AdminSeal;